import { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronRight, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import { useProjectStore } from '../store/useProjectStore';
import { useAppStore } from '../store/useAppStore';
import { useConfigStore } from '../store/useConfigStore';
import RefreshButton from '../components/common/RefreshButton';
import { workItemPageApi } from '../services/api';
import type { WorkItem } from '../types';

type WsjfRow = WorkItem & { children?: WsjfRow[] };

type SortColumn = 'title' | 'storyPoints' | 'jobSize' | 'businessValue' | 'timeCriticality' | 'riskReductionOpportunity' | 'wsjfScore' | 'estimatedHours' | 'loggedHours';
type SortDirection = 'asc' | 'desc';

/** Enough to fill the screen and then some. The top of a WSJF ranking is
 *  what gets acted on; the tail is available on request. */
const PAGE_SIZE = 100;

export default function WsjfPage({ projectId: scoped }: { projectId?: string } = {}) {
  const { fetchProjects } = useProjectStore();
  const { setDetailItemId, selectedProjectId: storeProjectId } = useAppStore();
  const selectedProjectId = scoped || storeProjectId;
  const { getWorkItemTypeConfig } = useConfigStore();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string>('all');
  const [sortColumn, setSortColumn] = useState<SortColumn>('wsjfScore');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  // Paged rather than the whole estate; see loadPage below.
  const [ranked, setRanked] = useState<any[]>([]);
  // Children, fetched per row when it is opened. They cannot come from the
  // ranked page: that page is one TYPE (see loadPage), so a root's children
  // are never in it.
  const [childrenById, setChildrenById] = useState<Record<string, WsjfRow[]>>({});
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [loadedOffset, setLoadedOffset] = useState(0);
  const [pageLoading, setPageLoading] = useState(false);

  const handleSort = (col: SortColumn) => {
    if (sortColumn === col) {
      setSortDirection((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortColumn(col);
      setSortDirection(col === 'title' ? 'asc' : 'desc');
    }
  };

  const SortIcon = ({ col }: { col: SortColumn }) => {
    if (sortColumn !== col) return null;
    return sortDirection === 'desc'
      ? <ChevronDown size={10} className="inline ml-0.5" />
      : <ChevronUp size={10} className="inline ml-0.5" />;
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // The type this view puts at the top level. It has to be part of the QUERY,
  // not a filter applied to the results.
  //
  // Ranking every type together and then keeping only epics left this page
  // permanently blank. WSJF is (BV+TC+RRO)/jobSize, and an epic's job size is
  // the rollup of everything beneath it — so epics score LOW by construction
  // while a 1-point task scores enormously. Measured on the live estate: the
  // best epic anywhere scores 13.0, and the 500th item overall scores 26.0.
  // No epic could ever appear in a top-N page, however far you scrolled.
  // Only levels that HAVE a portfolio meaning may root this view.
  //
  // Tasks and bugs are deliberately not among them. A WSJF score ranks
  // SIBLINGS — tasks within a story, stories within a feature — because that
  // is the only comparison with any meaning. A flat league of every task in
  // the estate ranks work from unrelated stories against each other and reads
  // as an order to work in, which it is not: it strips the context that makes
  // the number mean anything. Tasks are reached by opening the story that owns
  // them, where their ranking is against the siblings they actually compete
  // with, and where their points roll up.
  const ROOT_TYPES = ['epic', 'feature', 'story'];
  const rootType = ROOT_TYPES.includes(filterType) ? filterType : 'epic';

  // Priorities is genuinely cross-project — ranking a portfolio needs breadth,
  // so "pick a project first" is the wrong answer here. Instead of loading every
  // work item in the estate (EP-16 / BG-194), ask the server for the highest
  // WSJF scores in pages: the ranking is computed server-side, and the top of
  // the list is the part anyone actually looks at.
  const loadPage = useCallback(async (offset: number) => {
    setPageLoading(true);
    try {
      const page = await workItemPageApi.page(
        // Embedded for ONE project (the training module's Priorities tab), rank that
        // project server-side: filtering the estate's top page in the browser hid every
        // item outside it until "load more" (EP-26 FE-17 ST-1).
        { sortBy: 'wsjf', sortOrder: 'desc', workItemType: rootType,
          withDependencies: '1', ...(scoped ? { projectId: scoped } : {}) },
        PAGE_SIZE, offset);
      setRanked((prev) => (offset === 0 ? page.items : [...prev, ...page.items]));
      setHasMore(page.hasMore);
      setTotal(page.total);
      setLoadedOffset(offset + page.items.length);
    } finally {
      setPageLoading(false);
    }
    // Refetches when the type changes: the ranking is now per type, so
    // switching the filter is a different query rather than a different view
    // of the same rows.
  }, [rootType, scoped]);

  useEffect(() => { loadPage(0); }, [loadPage]);

  // Children are fetched when a row is opened, not with the page. The ranked
  // page holds one type, so a root's children are never in it — and fetching
  // every descendant up front is what this page was rewritten to stop doing.
  const toggleExpanded = useCallback(async (item: WsjfRow) => {
    const id = item._id;
    const opening = !expanded.has(id);
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    if (!opening || childrenById[id]) return;
    try {
      const page = await workItemPageApi.page(
        { parentId: id, sortBy: 'wsjf', sortOrder: 'desc',
          withDependencies: '1' }, PAGE_SIZE, 0);
      setChildrenById((prev) => ({ ...prev, [id]: page.items as WsjfRow[] }));
    } catch {
      // A row that will not open is better than a page that falls over.
      setChildrenById((prev) => ({ ...prev, [id]: [] }));
    }
  }, [expanded, childrenById]);

  const refresh = async () => {
    await fetchProjects();
    await loadPage(0);
  };

  // The rows, ranked. The SERVER already ranked and paged them by WSJF for
  // the one type this view roots on, so there is no tree to assemble here —
  // children arrive per row when it is opened.
  const rankedTree = useMemo(() => {
    const comparator = (a: WsjfRow, b: WsjfRow): number => {
      if (sortColumn === 'title') {
        const av = (a.title || '').toLowerCase();
        const bv = (b.title || '').toLowerCase();
        return sortDirection === 'asc' ? (av < bv ? -1 : av > bv ? 1 : 0)
                                       : (bv < av ? -1 : bv > av ? 1 : 0);
      }
      // On the WSJF column, compare the EFFECTIVE score — the server ranked
      // on it, and re-sorting here on the raw one would silently undo that.
      // Startable work stays ahead of blocked work either way.
      const pick = (x: WsjfRow) => sortColumn === 'wsjfScore'
        ? (x.effectiveWsjf ?? x.wsjfScore ?? 0)
        : ((x[sortColumn] as number) || 0);
      const blockedA = (a.openBlockers ?? 0) > 0 ? 1 : 0;
      const blockedB = (b.openBlockers ?? 0) > 0 ? 1 : 0;
      if (sortColumn === 'wsjfScore' && blockedA !== blockedB) return blockedA - blockedB;
      const av = pick(a);
      const bv = pick(b);
      return sortDirection === 'desc' ? bv - av : av - bv;
    };

    // Project scoping stays client-side: it narrows what was fetched rather
    // than changing the ranking, and the picker is meant to be instant.
    return (ranked as WsjfRow[])
      .filter((i) => !selectedProjectId || i.projectId === selectedProjectId)
      .slice()
      .sort(comparator);
  }, [ranked, selectedProjectId, sortColumn, sortDirection]);

  // Summary over what has been loaded. `total` above carries the true count
  // from the server, so the page never implies it has seen more than it has.
  const summary = useMemo(() => {
    const inScope = (i: any) =>
      !selectedProjectId || i.projectId === selectedProjectId;
    // `ranked` is now ONE type — whichever this view roots on — so the totals
    // are over that type. They used to single out epics, which read as zero on
    // the Feature and Story views: a total that silently means "nothing here"
    // is worse than no total.
    const rows = ranked.filter(inScope);
    const scored = rows.filter((i: any) => (i.wsjfScore || 0) > 0);
    return {
      scoredCount: scored.length,
      totalSP: rows.reduce((s: number, i: any) => s + (i.storyPoints || 0), 0),
      totalEst: rows.reduce((s: number, i: any) => s + (i.estimatedHours || 0), 0),
      totalLogged: rows.reduce((s: number, i: any) => s + (i.loggedHours || 0), 0),
    };
  }, [ranked, selectedProjectId]);

  const renderRow = (row: WsjfRow, depth: number = 0) => {
    const typeConfig = getWorkItemTypeConfig(row.workItemType);
    const isExpanded = expanded.has(row._id);
    const kids = childrenById[row._id];
    // Whether a row CAN be opened is not known until its children are
    // fetched, so offer the control on anything that could have them —
    // including stories, which is how you reach the TASKS. Opening something
    // childless shows "No children" rather than nothing happening.
    const canExpand = ['epic', 'feature', 'story'].includes(row.workItemType);

    return (
      <div key={row._id}>
        <div
          className="flex items-center gap-2 px-4 py-2 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer border-b border-gray-100 dark:border-gray-700/50"
          style={{ paddingLeft: `${16 + depth * 24}px` }}
          onClick={() => setDetailItemId(row._id)}
        >
          {/* Expand toggle */}
          <div className="w-5 shrink-0">
            {canExpand && (
              <button
                onClick={(e) => { e.stopPropagation(); toggleExpanded(row); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
              </button>
            )}
          </div>

          {/* Type badge */}
          <span
            className="text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase shrink-0"
            style={{ backgroundColor: typeConfig.color + '22', color: typeConfig.color }}
          >
            {row.workItemType.substring(0, 1)}
          </span>

          {/* Display ID */}
          <span className="text-xs text-gray-400 font-mono shrink-0 w-14">{row.displayId}</span>

          {/* Title */}
          <span className="text-sm text-gray-800 dark:text-gray-200 truncate flex-1">{row.title}</span>

          {/* Cross-tree dependencies. Sibling ORDER cannot say any of this:
              these point outside the parent, and they are the reason a ranking
              is not simply a queue. */}
          {(row.openBlockers ?? 0) > 0 && (
            <span
              title={(row.blockedBy || [])
                .map((b) => `${b.key || b._id} — ${b.title || 'unreadable'} (${b.status || 'unknown'}) · ${
                  (b as any).checkout ? `checked out by ${(b as any).checkout.holder} until ${String((b as any).checkout.until).slice(0, 16).replace('T', ' ')}` : 'not being worked'}`)
                .join(String.fromCharCode(10))}
              className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300"
            >
              blocked ×{row.openBlockers}
            </span>
          )}
          {(row.blocks ?? 0) > 0 && (
            <span
              title={`${row.blocks} item(s) are waiting on this — clearing it unblocks them`}
              className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-300"
            >
              blocks {row.blocks}
            </span>
          )}

          {/* SP */}
          <span className="text-xs text-gray-500 w-10 text-right">{row.storyPoints > 0 ? row.storyPoints : '—'}</span>

          {/* Job Size */}
          <span className="text-xs text-gray-500 w-12 text-right">{row.jobSize > 0 ? row.jobSize : '—'}</span>

          {/* BV */}
          <span className="text-xs text-gray-500 w-8 text-right">{row.businessValue > 0 ? row.businessValue : '—'}</span>

          {/* TC */}
          <span className="text-xs text-gray-500 w-8 text-right">{row.timeCriticality > 0 ? row.timeCriticality : '—'}</span>

          {/* RRO */}
          <span className="text-xs text-gray-500 w-8 text-right">{row.riskReductionOpportunity > 0 ? row.riskReductionOpportunity : '—'}</span>

          {/* WSJF. The EFFECTIVE score leads, because that is the order —
              but the raw one stays visible beside it whenever the two differ,
              so an item near the top on borrowed value never looks like it
              scored that way on its own. */}
          {(() => {
            const raw = row.wsjfScore || 0;
            const eff = row.effectiveWsjf ?? raw;
            const lifted = eff > raw + 0.05;
            return (
              <span
                className="w-14 text-right shrink-0"
                title={lifted
                  ? `Scores ${raw.toFixed(1)} on its own. Clearing it releases `
                    + `${row.unblockedCoD} of cost of delay across `
                    + `${row.delaysScoredItems} item(s), so its effective score is `
                    + `${eff.toFixed(1)}.`
                  : undefined}
              >
                <span className={`text-sm font-bold ${eff > 0 ? 'text-accent' : 'text-gray-300 dark:text-gray-600'}`}>
                  {eff > 0 ? eff.toFixed(1) : '—'}
                </span>
                {lifted && (
                  <span className="block text-[9px] leading-tight text-gray-400">
                    was {raw.toFixed(1)}
                  </span>
                )}
              </span>
            );
          })()}

          {/* Est Hours */}
          <span className="text-xs text-gray-500 w-12 text-right">{row.estimatedHours > 0 ? `${row.estimatedHours}h` : '—'}</span>

          {/* Logged Hours */}
          <span className="text-xs text-gray-500 w-12 text-right">{row.loggedHours > 0 ? `${row.loggedHours}h` : '—'}</span>
        </div>

        {/* Expanded children */}
        {isExpanded && (
          kids === undefined
            ? <div className="text-xs text-gray-400 py-1"
                   style={{ paddingLeft: `${16 + (depth + 1) * 24}px` }}>Loading…</div>
            : kids.length === 0
              ? <div className="text-xs text-gray-400 py-1"
                     style={{ paddingLeft: `${16 + (depth + 1) * 24}px` }}>No children</div>
              : kids.map((child) => renderRow(child, depth + 1))
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <header className="shrink-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp size={20} className="text-accent" />
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">WSJF Prioritization</h1>
          </div>
          <div className="flex items-center gap-3">
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-200 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-accent"
            >
              {/* Each option is a different QUERY now, not a filter over one
                  result set — ranking is per type, because WSJF is not
                  comparable across them. "Epics" is the portfolio view;
                  expand a row to see what sits under it. */}
              {/* No Tasks or Bugs option: a flat league across the estate
                  ranks work from unrelated stories against each other. They
                  are reached by opening the story that owns them, where the
                  comparison is against the siblings they compete with. */}
              <option value="all">Epics (expand → features → stories → tasks)</option>
              <option value="feature">Features</option>
              <option value="story">Stories</option>
            </select>
            <RefreshButton onRefresh={refresh} tooltip="Refresh work items" />
          </div>
        </div>

        {/* Summary stats */}
        <div className="flex items-center gap-6 mt-3 text-xs text-gray-500 dark:text-gray-400">
          <span>Scored items: <strong className="text-gray-800 dark:text-gray-200">{summary.scoredCount}</strong></span>
          <span>Total SP: <strong className="text-gray-800 dark:text-gray-200">{summary.totalSP}</strong></span>
          <span>Est: <strong className="text-gray-800 dark:text-gray-200">{summary.totalEst}h</strong></span>
          <span>Logged: <strong className="text-gray-800 dark:text-gray-200">{summary.totalLogged}h</strong></span>
          {summary.totalEst > 0 && (
            <span>Variance: <strong className={summary.totalEst - summary.totalLogged >= 0 ? 'text-green-600' : 'text-red-600'}>
              {(summary.totalEst - summary.totalLogged).toFixed(0)}h
            </strong></span>
          )}
        </div>
      </header>

      {/* Table header */}
      <div className="shrink-0 flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider select-none">
        <div className="w-5 shrink-0" />
        <div className="w-7 shrink-0" />
        <div className="w-14 shrink-0">ID</div>
        <div className="flex-1 cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('title')}>Title<SortIcon col="title" /></div>
        <div className="w-10 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('storyPoints')}>SP<SortIcon col="storyPoints" /></div>
        <div className="w-12 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('jobSize')}>Job<SortIcon col="jobSize" /></div>
        <div className="w-8 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('businessValue')}>BV<SortIcon col="businessValue" /></div>
        <div className="w-8 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('timeCriticality')}>TC<SortIcon col="timeCriticality" /></div>
        <div className="w-8 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('riskReductionOpportunity')}>RRO<SortIcon col="riskReductionOpportunity" /></div>
        <div className="w-14 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('wsjfScore')}>WSJF<SortIcon col="wsjfScore" /></div>
        <div className="w-12 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('estimatedHours')}>Est<SortIcon col="estimatedHours" /></div>
        <div className="w-12 text-right cursor-pointer hover:text-gray-700 dark:hover:text-gray-200" onClick={() => handleSort('loggedHours')}>Logged<SortIcon col="loggedHours" /></div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto">
        {rankedTree.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-400 dark:text-gray-500">
            <TrendingUp size={40} className="mb-3" />
            <p className="text-sm">No WSJF data yet. Score your work items to see rankings.</p>
          </div>
        ) : (
          <>
            {rankedTree.map((row) => renderRow(row))}

            {/* Ranked server-side, so the top of the list is complete and
                correct without having loaded the estate. */}
            <div className="flex items-center justify-center gap-3 py-4 text-xs text-gray-500 dark:text-gray-400">
              <span>
                Showing {ranked.length} of {total} scored items, highest WSJF first
              </span>
              {hasMore && (
                <button
                  onClick={() => loadPage(loadedOffset)}
                  disabled={pageLoading}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent disabled:opacity-50"
                >
                  {pageLoading ? 'Loading…' : 'Load more'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
